import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
    Box,
    Checkbox,
    Flex,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Skeleton,
    SkeletonText,
    Tab,
    useMediaQuery,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
} from '@chakra-ui/react'
import parse from 'html-react-parser'
import { useTranslation } from 'react-i18next'

import { useGetPolicyQuery } from '@core/app/api/globalApi'
import { useAcceptTermsMutation, useGetProfileDataQuery } from '@core/app/api/v2/newProfileApi'
import { MainButton } from '@core/components/MainButton'

type PolicyType = 'user-agreement' | 'privacy-policy' | 'cookie-notice'

const POLICY_HTML_SX = {
    fontFamily: "'Open Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '18px',
    color: '#0C0C0D',
    'h1, h2, h4, h5, h6': {
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '18px',
        mt: '12px',
        mb: '4px',
        color: '#0C0C0D',
    },
    h3: {
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '18px',
        m: 0,
        p: 0,
        color: '#0C0C0D',
    },
    'h1:first-child, h2:first-child, h3:first-child, h4:first-child, h5:first-child, h6:first-child, p:first-child': {
        mt: 0,
    },
    'p:last-child, ul:last-child, ol:last-child': {
        mb: 0,
    },
    p: {
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '18px',
        m: 0,
        p: 0,
        color: '#0C0C0D',
    },
    'ul, ol': {
        pl: '20px',
        my: 0,
    },
    li: {
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '18px',
        m: 0,
        color: '#0C0C0D',
    },
    a: {
        color: 'text.link',
        textDecoration: 'underline',
        _hover: { opacity: 0.8 },
    },
    strong: { fontWeight: 600 },
    em: { fontStyle: 'italic' },
}

const CHECK_PATH =
    'M14.4643 3.52178C14.7285 3.77821 14.7348 4.20027 14.4784 4.46448L6.71368 12.4645C6.58813 12.5938 6.41556 12.6668 6.23529 12.6668C6.05503 12.6668 5.88245 12.5938 5.75691 12.4645L1.52161 8.10084C1.26518 7.83664 1.27148 7.41458 1.53568 7.15814C1.79989 6.9017 2.22195 6.908 2.47839 7.17221L6.23529 11.043L13.5216 3.53585C13.778 3.27164 14.2001 3.26534 14.4643 3.52178Z'

const CheckIcon = ({ color }: { color: string }) => (
    <Box as="span" display="flex" w="16px" h="16px" flexShrink={0} color={color}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d={CHECK_PATH} fill="currentColor" />
        </svg>
    </Box>
)

const CHECK_MASK =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill-rule='evenodd' d='M14.4643 3.52178C14.7285 3.77821 14.7348 4.20027 14.4784 4.46448L6.71368 12.4645C6.58813 12.5938 6.41556 12.6668 6.23529 12.6668C6.05503 12.6668 5.88245 12.5938 5.75691 12.4645L1.52161 8.10084C1.26518 7.83664 1.27148 7.41458 1.53568 7.15814C1.79989 6.9017 2.22195 6.908 2.47839 7.17221L6.23529 11.043L13.5216 3.53585C13.778 3.27164 14.2001 3.26534 14.4643 3.52178Z'/%3E%3C/svg%3E\")"

const CHECK_MARK = {
    content: '""',
    position: 'absolute',
    w: '12px',
    h: '12px',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    opacity: 0,
    bg: '#CBCBCD',
    pointerEvents: 'none',
    transition: 'none',
    maskImage: CHECK_MASK,
    maskSize: 'contain',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskImage: CHECK_MASK,
    WebkitMaskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
} as const

const PolicyPanel = ({ type, enabled }: { type: PolicyType; enabled: boolean }) => {
    const { data, isLoading } = useGetPolicyQuery({ type }, { skip: !enabled })

    if (isLoading) {
        return (
            <Flex direction="column" gap="16px">
                <Skeleton h="24px" w="60%" borderRadius="4px" />
                <SkeletonText noOfLines={4} spacing="3" skeletonHeight="14px" />
                <SkeletonText noOfLines={3} spacing="3" skeletonHeight="14px" />
                <Skeleton h="24px" w="40%" borderRadius="4px" />
                <SkeletonText noOfLines={5} spacing="3" skeletonHeight="14px" />
            </Flex>
        )
    }

    if (!data?.data?.text) return null

    return <Box sx={POLICY_HTML_SX}>{parse(data.data.text)}</Box>
}

const primaryVoters = new Set<(isPrimary: boolean) => void>()

const electPrimaryInstance = () => {
    const primary = primaryVoters.values().next().value
    primaryVoters.forEach((vote) => vote(vote === primary))
}

export const TermsAndPoliciesGate = ({ children }: { children: ReactNode }) => (
    <>
        <TermsAndPoliciesModal />
        {children}
    </>
)

export const TermsAndPoliciesModal = () => {
    const { t } = useTranslation()
    const [isDesktop] = useMediaQuery('(min-width: 768px)', { fallback: true })
    const [isPrimaryInstance, setIsPrimaryInstance] = useState(false)
    const { data, isSuccess } = useGetProfileDataQuery({ fields: 'terms' }, { skip: !isPrimaryInstance })
    const [acceptTerms, { isLoading: isAccepting }] = useAcceptTermsMutation()
    const [agreed, setAgreed] = useState(false)
    const [hasAccepted, setHasAccepted] = useState(false)
    const [tabIndex, setTabIndex] = useState(0)
    const bodyRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const modalContentRef = useRef<HTMLDivElement>(null)
    const [lockedModalH, setLockedModalH] = useState(0)
    const [showFooterDivider, setShowFooterDivider] = useState(false)

    const updateFooterDivider = useCallback(() => {
        const el = bodyRef.current
        if (!el) {
            setShowFooterDivider(false)
            return
        }

        const hasOverflow = el.scrollHeight > el.clientHeight + 1
        const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 1
        setShowFooterDivider(hasOverflow && !isAtBottom)
    }, [])

    const isTermsAccepted = data?.terms?.accepted
    const isOpen = isSuccess && isTermsAccepted === false && !hasAccepted
    const canAccept = agreed && !isAccepting

    useLayoutEffect(() => {
        primaryVoters.add(setIsPrimaryInstance)
        electPrimaryInstance()
        return () => {
            primaryVoters.delete(setIsPrimaryInstance)
            electPrimaryInstance()
        }
    }, [])

    useEffect(() => {
        if (!isOpen) {
            setShowFooterDivider(false)
            setLockedModalH(0)
            return
        }

        const lockHeight = () => {
            const el = modalContentRef.current
            if (!el) return
            const next = el.offsetHeight
            setLockedModalH((prev) => (next > prev ? next : prev))
        }

        lockHeight()
        const lockFrame = requestAnimationFrame(lockHeight)

        let cancelled = false
        let observer: ResizeObserver | null = null
        let mutationObserver: MutationObserver | null = null
        let body: HTMLDivElement | null = null

        const setup = () => {
            if (cancelled) return
            body = bodyRef.current
            const content = contentRef.current
            if (!body) {
                requestAnimationFrame(setup)
                return
            }

            updateFooterDivider()
            lockHeight()
            body.addEventListener('scroll', updateFooterDivider, { passive: true })
            window.addEventListener('resize', updateFooterDivider)

            observer = new ResizeObserver(() => {
                updateFooterDivider()
                lockHeight()
            })
            observer.observe(body)
            if (content) observer.observe(content)

            mutationObserver = new MutationObserver(updateFooterDivider)
            mutationObserver.observe(body, { childList: true, subtree: true })
        }

        const frame = requestAnimationFrame(setup)

        return () => {
            cancelled = true
            cancelAnimationFrame(lockFrame)
            cancelAnimationFrame(frame)
            body?.removeEventListener('scroll', updateFooterDivider)
            window.removeEventListener('resize', updateFooterDivider)
            observer?.disconnect()
            mutationObserver?.disconnect()
        }
    }, [isOpen, tabIndex, updateFooterDivider])

    const handleAccept = async () => {
        if (!canAccept) return
        try {
            await acceptTerms().unwrap()
            setHasAccepted(true)
        } catch {
            // Keep the modal open if acceptance was not recorded
        }
    }

    const tabs = [
        { label: t('4635', 'User agreement'), type: 'user-agreement' as const },
        { label: t('4636', 'Privacy policy'), type: 'privacy-policy' as const },
        { label: t('4637', 'Cookie notice'), type: 'cookie-notice' as const },
    ]

    if (!isPrimaryInstance) return null

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => undefined}
            closeOnOverlayClick={false}
            closeOnEsc={false}
            isCentered={isDesktop}
            trapFocus
            blockScrollOnMount
            autoFocus={false}
            isLazy
            scrollBehavior="inside"
            size="xl"
        >
            <ModalOverlay bg="rgba(0, 0, 0, 0.45)" />
            <ModalContent
                ref={modalContentRef}
                w={{ base: '100%', md: '664px' }}
                maxW={{ base: '100%', md: 'calc(100vw - 32px)' }}
                h={lockedModalH ? `${lockedModalH}px` : 'auto'}
                maxH={{ base: 'calc(100dvh - 32px)', md: 'calc(100dvh - 64px)' }}
                borderRadius={{ base: '12px 12px 0 0', md: '12px' }}
                overflow="hidden"
                display="flex"
                flexDirection="column"
                mt="32px"
                mb={{ base: 0, md: '32px' }}
                mx={{ base: 0, md: 'auto' }}
                alignSelf={{ base: 'flex-end', md: 'center' }}
                boxShadow="none"
            >
                <Tabs
                    index={tabIndex}
                    onChange={setTabIndex}
                    variant="unstyled"
                    display="flex"
                    flexDirection="column"
                    flex="1 1 auto"
                    minH={0}
                    overflow="hidden"
                >
                    <ModalHeader p={0} flexShrink={0}>
                        <Flex direction="column" pt="20px">
                            <Flex align="center" px="24px" h="28px" mb="6px">
                                <Text
                                    fontFamily="'Open Sans', sans-serif"
                                    fontWeight={600}
                                    fontSize="18px"
                                    lineHeight="28px"
                                    letterSpacing="0px"
                                    color="#0C0C0D"
                                    display="flex"
                                    alignItems="center"
                                >
                                    {t('11382', 'Terms and policies')}
                                </Text>
                            </Flex>
                            <TabList
                                px="24px"
                                h="36px"
                                gap="12px"
                                borderBottom="1px solid #EAEAEB"
                                alignItems="flex-end"
                            >
                                {tabs.map((tab) => (
                                    <Tab
                                        key={tab.type}
                                        px={0}
                                        py="4px"
                                        mb="-1px"
                                        h="36px"
                                        minW="auto"
                                        fontFamily="'Open Sans', sans-serif"
                                        fontWeight={500}
                                        fontSize="13px"
                                        lineHeight="18px"
                                        letterSpacing="0px"
                                        color="#4B4B4E"
                                        borderRadius={0}
                                        borderBottom="2px solid transparent"
                                        outline="none"
                                        WebkitTapHighlightColor="transparent"
                                        transition="color 200ms ease-out"
                                        _selected={{
                                            color: '#0C0C0D',
                                            borderBottomColor: '#0C0C0D',
                                        }}
                                        _hover={{ color: '#0C0C0D' }}
                                        _active={{
                                            bg: 'transparent',
                                            color: '#0C0C0D',
                                            boxShadow: 'none',
                                            outline: 'none',
                                        }}
                                        _focus={{ boxShadow: 'none', outline: 'none' }}
                                        _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                                        sx={{
                                            '&:hover .tab-item': {
                                                bg: '#F5F5F5',
                                            },
                                            '&:not([aria-selected=true]):hover': {
                                                color: '#0C0C0D',
                                            },
                                            _active: {
                                                '.tab-item': {
                                                    boxShadow: 'none',
                                                },
                                            },
                                            _focusVisible: {
                                                '.tab-item': {
                                                    bg: '#FFFFFF',
                                                    boxShadow:
                                                        '0px 0px 0px 2px rgba(36, 90, 240, 0.4), inset 0px 0px 0px 1px #245AF0',
                                                },
                                            },
                                        }}
                                    >
                                        <Box
                                            className="tab-item"
                                            as="span"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            h="28px"
                                            px="6px"
                                            mx="-6px"
                                            borderRadius="6px"
                                            color="inherit"
                                            transition="background-color 200ms ease-out, box-shadow 200ms ease-out"
                                        >
                                            {tab.label}
                                        </Box>
                                    </Tab>
                                ))}
                            </TabList>
                        </Flex>
                    </ModalHeader>
                    <ModalBody ref={bodyRef} p={0} flex="1 1 auto" minH={0} overflowY="auto">
                        <Box ref={contentRef} px="24px" pt="20px" pb={0}>
                            <TabPanels>
                                {tabs.map((tab) => (
                                    <TabPanel key={tab.type} p={0}>
                                        <PolicyPanel type={tab.type} enabled={isOpen} />
                                    </TabPanel>
                                ))}
                            </TabPanels>
                        </Box>
                    </ModalBody>
                    <ModalFooter
                        flexShrink={0}
                        px="24px"
                        py="20px"
                        minH="76px"
                        borderTop={showFooterDivider ? '1px solid #EAEAEB' : '1px solid transparent'}
                        flexDirection={{ base: 'column', md: 'row' }}
                        justifyContent={{ base: 'flex-start', md: 'space-between' }}
                        alignItems={{ base: 'stretch', md: 'center' }}
                        gap={{ base: '16px', md: '8px' }}
                    >
                        <Checkbox
                            isChecked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            onMouseUp={(e) => {
                                e.currentTarget.querySelector('input')?.blur()
                            }}
                            alignItems="center"
                            icon={<Box as="span" display="none" />}
                            sx={{
                                cursor: 'pointer',
                                '.chakra-checkbox__control': {
                                    boxSizing: 'border-box',
                                    w: '20px',
                                    h: '20px',
                                    minW: '20px',
                                    borderRadius: '4px',
                                    borderWidth: '1px',
                                    borderStyle: 'solid',
                                    borderColor: '#97979B',
                                    bg: '#FFFFFF',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    transition: 'none',
                                    '--chakra-colors-blue-500': '#245AF0',
                                    _after: CHECK_MARK,
                                    _focus: { boxShadow: 'none' },
                                    _focusVisible: {
                                        boxShadow:
                                            '0px 0px 0px 2px rgba(36, 90, 240, 0.4), inset 0px 0px 0px 1px #245AF0',
                                        borderColor: '#245AF0',
                                    },
                                    _active: {
                                        bg: '#FFFFFF',
                                        borderColor: '#245AF0',
                                        boxShadow: 'none',
                                    },
                                    _checked: {
                                        bg: '#245AF0',
                                        borderColor: '#245AF0',
                                        _hover: {
                                            bg: '#1D48C0',
                                            borderColor: '#1D48C0',
                                        },
                                        _focus: { boxShadow: 'none' },
                                        _focusVisible: {
                                            boxShadow: '0px 0px 0px 2px rgba(36, 90, 240, 0.4)',
                                        },
                                        _active: {
                                            bg: '#1D48C0',
                                            borderColor: '#1D48C0',
                                            boxShadow: 'none',
                                        },
                                    },
                                    _disabled: {
                                        bg: '#F5F5F5',
                                        borderColor: '#CBCBCD',
                                        _checked: {
                                            bg: '#CBCBCD',
                                            borderColor: '#CBCBCD',
                                        },
                                    },
                                },
                                '.chakra-checkbox__icon': {
                                    display: 'none !important',
                                    opacity: '0 !important',
                                    w: '0 !important',
                                    h: '0 !important',
                                    overflow: 'hidden',
                                    transition: 'none',
                                },
                                '&:hover:not([data-checked]) .chakra-checkbox__control': {
                                    borderColor: '#245AF0',
                                    _after: {
                                        opacity: 1,
                                        bg: '#CBCBCD',
                                    },
                                },
                                '&[data-checked] .chakra-checkbox__control': {
                                    bg: '#245AF0',
                                    borderColor: '#245AF0',
                                    _after: {
                                        opacity: 1,
                                        bg: '#FFFFFF',
                                    },
                                },
                                '&[data-checked]:hover .chakra-checkbox__control': {
                                    bg: '#1D48C0',
                                    borderColor: '#1D48C0',
                                    _after: {
                                        opacity: 1,
                                        bg: '#FFFFFF',
                                    },
                                },
                                '.chakra-checkbox__label': {
                                    ml: '8px',
                                    cursor: 'pointer',
                                },
                            }}
                        >
                            <Text
                                fontFamily="'Open Sans', sans-serif"
                                fontWeight={500}
                                fontSize="13px"
                                lineHeight="18px"
                                color="#4B4B4E"
                            >
                                {t('I have read and agree to the', 'I have read and agree to the')}{' '}
                                {t('11382', 'Terms and policies')}
                            </Text>
                        </Checkbox>
                        <MainButton
                            view="primary"
                            size="sm"
                            title={t('591', 'Accept') as string}
                            leftIcon={<CheckIcon color={canAccept ? '#FFFFFF' : '#CBCBCD'} />}
                            iconSpacing={0}
                            isDisabled={!canAccept}
                            isLoading={isAccepting}
                            onClick={handleAccept}
                            h="36px"
                            w={{ base: '100%', md: 'auto' }}
                            px="16px"
                            gap="8px"
                            borderRadius="8px"
                            flexShrink={0}
                            ml={{ base: 0, md: 'auto' }}
                            fontFamily="'Open Sans', sans-serif"
                            fontWeight={500}
                            fontSize="13px"
                            lineHeight="18px"
                            letterSpacing="0px"
                            color="#FFFFFF"
                            sx={{
                                '.chakra-button__icon': { mr: 0, ml: 0 },
                                '.chakra-text': {
                                    fontFamily: "'Open Sans', sans-serif",
                                    fontWeight: 500,
                                    fontSize: '13px',
                                    lineHeight: '18px',
                                    letterSpacing: '0px',
                                    color: 'inherit',
                                },
                                _disabled: {
                                    opacity: 1,
                                    bg: '#F5F5F5',
                                    color: '#B6B6B9',
                                    cursor: 'not-allowed',
                                    _hover: { bg: '#F5F5F5' },
                                },
                            }}
                        />
                    </ModalFooter>
                </Tabs>
            </ModalContent>
        </Modal>
    )
}
